import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL } from '../config/api';
import { trackEvent } from '../utils/analytics';
import { usePageMeta } from '../hooks/usePageMeta';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { formatISODate } from '../components/badges';
import {
  MARKER_TITLES,
  ParsedMrl,
  mrlText,
  normName,
  parseMrl,
  shortReg,
  splitResidueName,
  tidyName,
} from '../utils/pesticide';

/* ── API shapes (unchanged endpoint) ────────────────────────────────── */

interface SubstanceInfo {
  pesticide_residue_id: string;
  name: string;
  tier2name: string | null;
}

interface ProductEntry {
  product_id: string;
  product_parent_id: string | null;
  product_name: string;
  product_code: string | null;
  mrl_value: string | null;
  regulation_number: string | null;
  regulation_url: string | null;
  application_date: string | null;
  status: 'in-force' | 'upcoming' | 'category';
  upcoming_mrl: string | null;
  upcoming_reg: string | null;
  upcoming_date: string | null;
}

interface ToxEntry {
  value: string | null;
  source: string | null;
  remark: string | null;
}

interface ActiveSubstance {
  substance_id: string | null;
  substance_name: string | null;
  substance_status: string | null;
  cas_number: string | null;
  category: string | null;
  category_short: string | null;
  low_risk: boolean;
  candidate_for_substitution: boolean;
  tox: {
    adi: ToxEntry;
    arfd: ToxEntry;
    aoel: ToxEntry;
    aaoel: ToxEntry;
  };
  classification_reg_1272: string | null;
  risk_assessment: string | null;
  authorisations_at_nat_level: string | null;
}

interface RegulationRef {
  regulation_number: string;
  regulation_url: string | null;
  application_date: string | null;
  product_count: number;
  status: 'in-force' | 'historical' | 'upcoming' | 'unknown';
  news_only?: boolean;
  post_title?: string | null;
  published_date?: string | null;
  celex_number?: string | null;
}

interface SubstanceData {
  substance: SubstanceInfo;
  residue_definition: string | null;
  regulations: RegulationRef[];
  actives: ActiveSubstance[];
  products: ProductEntry[];
}

/* ── View model ─────────────────────────────────────────────────────── */

interface Row {
  product_id: string;
  name: string;
  code: string | null;
  parent: string | null;
  mrl: ParsedMrl;
  regulation_number: string | null;
  application_date: string | null;
  upcoming: { mrl: ParsedMrl; reg: string | null; date: string | null } | null;
  key: string;
}

type Tab = 'mrls' | 'upcoming' | 'regulations' | 'data';
type MrlView = 'set' | 'all' | 'lod';

const STATUS_RANK: Record<string, number> = { Approved: 1, Pending: 2, 'Not approved': 3 };
const KIND_RANK: Record<ParsedMrl['kind'], number> = { set: 0, lod: 1, exempt: 2, none: 3 };

async function fetchSubstance(residueId: string): Promise<SubstanceData> {
  const res = await fetch(`${API_BASE_URL}/api/substance/${encodeURIComponent(residueId)}`);
  if (res.status === 404) throw new Error('This substance is not in the MRL database.');
  if (!res.ok) throw new Error(`Could not load this substance (HTTP ${res.status}).`);
  const json = await res.json();
  if (!json.ok) throw new Error('The server could not load this substance.');
  return {
    substance: json.substance,
    residue_definition: json.residue_definition,
    regulations: json.regulations ?? [],
    actives: json.actives ?? [],
    products: json.products ?? [],
  };
}

function statusClass(status: string | null): string {
  switch (status) {
    case 'Approved': return 'substance-pill substance-pill--approved';
    case 'Pending': return 'substance-pill substance-pill--pending';
    case 'Not approved': return 'substance-pill substance-pill--banned';
    default: return 'substance-pill substance-pill--unknown';
  }
}

function bestStatus(actives: ActiveSubstance[]): string | null {
  let best: string | null = null;
  for (const a of actives) {
    const s = a.substance_status;
    if (!s) continue;
    if (best == null || (STATUS_RANK[s] ?? 4) < (STATUS_RANK[best] ?? 4)) best = s;
  }
  return best;
}

function ToxRow({ label, entry }: { label: string; entry: ToxEntry }) {
  if (!entry.value) return null;
  const isNA = entry.value.trim().toLowerCase() === 'not appl.';
  /* Source data occasionally stores a literal "None" unit (e.g. "0.5 None") —
     show just the number rather than a non-unit. */
  const display = entry.value.replace(/(\d)\s+None\s*$/i, '$1');
  return (
    <div className="substance-tox__row">
      <div className="substance-tox__label">{label}</div>
      <div className="substance-tox__body">
        <div className={isNA ? 'substance-tox__value substance-tox__value--na' : 'substance-tox__value'}>
          {isNA ? 'Not applicable' : display}
        </div>
        {entry.source && <div className="substance-tox__source">Source: {entry.source}</div>}
        {entry.remark && <div className="substance-tox__remark">{entry.remark}</div>}
      </div>
    </div>
  );
}

function ActiveCard({ active }: { active: ActiveSubstance }) {
  const [toxOpen, setToxOpen] = useState(false);
  const tox = active.tox;
  const rows = [
    { label: 'ADI', entry: tox.adi },
    { label: 'ARfD', entry: tox.arfd },
    { label: 'AOEL', entry: tox.aoel },
    { label: 'AAOEL', entry: tox.aaoel },
  ];
  const hasAnyTox = rows.some(r => r.entry.value);
  const extras = [
    { label: 'CLP classification', value: active.classification_reg_1272 },
    { label: 'Risk assessment', value: active.risk_assessment },
    { label: 'National authorisations', value: active.authorisations_at_nat_level },
  ].filter(e => e.value && e.value.trim() !== '');

  return (
    <div className="substance-card">
      <div className="substance-card__head">
        <span className="substance-card__name">{active.substance_name}</span>
        {active.substance_status && (
          <span className={statusClass(active.substance_status)}>{active.substance_status}</span>
        )}
        {active.category_short && (
          <span className="substance-chip substance-chip--category">{active.category_short}</span>
        )}
        {active.cas_number && <span className="substance-card__cas">CAS {active.cas_number}</span>}
      </div>

      {(active.candidate_for_substitution || active.low_risk) && (
        <div className="substance-card__flags">
          {active.candidate_for_substitution && (
            <span className="substance-chip substance-chip--candidate">Candidate for substitution</span>
          )}
          {active.low_risk && (
            <span className="substance-chip substance-chip--low-risk">Low-risk active substance</span>
          )}
        </div>
      )}

      <button
        type="button"
        className="substance-tox__toggle"
        aria-expanded={toxOpen}
        onClick={() => setToxOpen(o => !o)}
      >
        <span className="substance-tox__caret">{toxOpen ? '▼' : '▶'}</span>
        Toxicology values
      </button>

      {toxOpen && (
        <div className="substance-tox__panel">
          {hasAnyTox ? (
            rows.map(r => <ToxRow key={r.label} label={r.label} entry={r.entry} />)
          ) : (
            <div className="substance-tox__empty">No toxicology values established.</div>
          )}
        </div>
      )}

      {extras.length > 0 && (
        <dl className="pest-detail__grid">
          {extras.map(e => (
            <div key={e.label}>
              <dt>{e.label}</dt>
              <dd>{e.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────── */

export default function SubstancePage() {
  const { residueId = '' } = useParams<{ residueId: string }>();

  const { data, isLoading, isError, error } = useQuery<SubstanceData, Error>(
    ['substance', residueId],
    () => fetchSubstance(residueId),
    { enabled: !!residueId },
  );

  const [tab, setTab] = useState<Tab>('mrls');
  const [view, setView] = useState<MrlView>('set');
  const [filter, setFilter] = useState('');
  const [showHistorical, setShowHistorical] = useState(false);

  useEffect(() => {
    setTab('mrls');
    setView('set');
    setFilter('');
    setShowHistorical(false);
  }, [residueId]);

  useEffect(() => {
    if (data) trackEvent('Substance View', { substance: residueId });
  }, [data, residueId]);

  const metaName = data ? splitResidueName(data.substance.name).name : null;
  usePageMeta({
    title: metaName ? `${metaName}: EU pesticide MRLs` : 'Substance',
    description: metaName
      ? `Current EU maximum residue levels (MRLs) for ${metaName} by product, with the regulation behind each limit and upcoming changes.`
      : null,
    noindex: isError,
  });

  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    const names = new Map(data.products.map(p => [p.product_id, tidyName(p.product_name)]));
    return data.products
      .filter(p => p.status !== 'category')
      .map(p => ({
        product_id: p.product_id,
        name: tidyName(p.product_name),
        code: p.product_code,
        parent: p.product_parent_id ? names.get(p.product_parent_id) ?? null : null,
        mrl: parseMrl(p.mrl_value),
        regulation_number: p.regulation_number,
        application_date: p.application_date,
        upcoming: p.upcoming_mrl
          ? { mrl: parseMrl(p.upcoming_mrl), reg: p.upcoming_reg, date: p.upcoming_date }
          : null,
        key: normName(`${p.product_name} ${p.product_code ?? ''}`),
      }))
      .sort((a, b) =>
        KIND_RANK[a.mrl.kind] - KIND_RANK[b.mrl.kind]
        || (a.code ?? '').localeCompare(b.code ?? '')
        || a.name.localeCompare(b.name));
  }, [data]);

  const counts = useMemo(() => {
    let set = 0;
    let lod = 0;
    let upcoming = 0;
    for (const r of rows) {
      if (r.mrl.kind === 'set') set++;
      else if (r.mrl.kind === 'lod') lod++;
      if (r.upcoming) upcoming++;
    }
    return { set, lod, upcoming, all: rows.length };
  }, [rows]);

  const range = useMemo(() => {
    const vals = rows.filter(r => r.mrl.kind === 'set' && r.mrl.value != null).map(r => r.mrl.value as number);
    if (vals.length === 0) return null;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    return min === max ? `${min}` : `${min} – ${max}`;
  }, [rows]);

  const upcomingRows = useMemo(() => rows.filter(r => r.upcoming), [rows]);

  /* A banned substance with one MRL repeated across every product needs a
     verdict, not hundreds of identical rows. */
  const verdict = useMemo(() => {
    if (!data) return null;
    if (bestStatus(data.actives) !== 'Not approved') return null;
    // A pending change makes a flat "every product" claim wrong.
    if (counts.upcoming > 0) return null;
    const priced = rows.filter(r => r.mrl.kind !== 'none');
    if (priced.length === 0) return null;
    const distinct = new Set(priced.map(r => r.mrl.raw));
    if (distinct.size !== 1) return null;
    const only = parseMrl(Array.from(distinct)[0]);
    if (only.value == null) return null;
    const regs = new Map<string, number>();
    for (const r of priced) {
      if (r.regulation_number) regs.set(r.regulation_number, (regs.get(r.regulation_number) ?? 0) + 1);
    }
    const topReg = Array.from(regs.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
    const dates = priced.map(r => r.application_date).filter((d): d is string => !!d).sort();
    return {
      value: only,
      count: priced.length,
      reg: topReg,
      from: dates.length ? dates[dates.length - 1] : null,
    };
  }, [data, rows, counts.upcoming]);

  const shown = useMemo(() => {
    const term = normName(filter);
    return rows.filter(r => {
      if (view === 'set' && r.mrl.kind !== 'set') return false;
      if (view === 'lod' && r.mrl.kind !== 'lod') return false;
      if (term && !r.key.includes(term)) return false;
      return true;
    });
  }, [rows, view, filter]);

  if (!residueId) return <p className="pest-page">No substance specified.</p>;
  if (isLoading) return <LoadingSkeleton rows={8} />;
  if (isError || !data) {
    return <div className="error-state">{error?.message ?? 'Could not load this substance.'}</div>;
  }

  const { name: displayName, markers } = splitResidueName(data.substance.name);
  const status = bestStatus(data.actives);
  const category = data.actives.find(a => a.category_short)?.category_short ?? null;
  const cas = data.actives.find(a => a.cas_number)?.cas_number ?? null;

  const inForceRegs = data.regulations.filter(r => !r.news_only && r.status === 'in-force');
  const upcomingRegs = data.regulations.filter(r => !r.news_only && r.status === 'upcoming');
  const historicalRegs = data.regulations.filter(r => !r.news_only && (r.status === 'historical' || r.status === 'unknown'));
  const newsRegs = data.regulations.filter(r => r.news_only);

  function onTabKey(e: React.KeyboardEvent<HTMLButtonElement>, order: Tab[]) {
    const i = order.indexOf(tab);
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const next = e.key === 'ArrowRight' ? (i + 1) % order.length : (i - 1 + order.length) % order.length;
      setTab(order[next]);
    }
  }

  const tabs: Array<[Tab, string]> = [
    ['mrls', `MRLs by product (${counts.all})`],
    ['upcoming', `Upcoming changes (${counts.upcoming})`],
    ['regulations', `Regulations (${data.regulations.length})`],
    ['data', data.actives.length === 1 ? 'Substance data' : `Substance data (${data.actives.length})`],
  ];

  const views: Array<[MrlView, string]> = [
    ['set', `Set MRLs (${counts.set})`],
    ['all', `All (${counts.all})`],
    ['lod', `LOD defaults (${counts.lod})`],
  ];

  function renderRegItem(r: RegulationRef) {
    if (r.news_only) {
      return (
        <li
          key={`${r.regulation_number}-news`}
          className="substance-regs__item substance-regs__item--news-only"
          title="Published but MRL data not yet available from the EU Pesticides Database. Opens EUR-Lex in a new tab."
        >
          <span className="substance-regs__pill substance-regs__pill--news">News · EUR-Lex</span>
          <a className="substance-regs__link" href={r.regulation_url ?? '#'} target="_blank" rel="noopener noreferrer">
            {r.regulation_number}
          </a>
          <span className="substance-regs__meta">
            {r.published_date ? `published ${r.published_date}` : ''}
            {r.post_title ? ` · ${r.post_title}` : ''}
          </span>
          <span className="substance-regs__news-note">MRL data pending upstream</span>
        </li>
      );
    }
    return (
      <li key={r.regulation_number} className={`substance-regs__item substance-regs__item--${r.status}`}>
        <span className={`substance-regs__pill substance-regs__pill--${r.status}`}>
          {r.status === 'in-force' ? 'In force' : r.status === 'upcoming' ? 'Upcoming' : 'Historical'}
        </span>
        <Link className="substance-regs__link" to={`/regulation/${encodeURIComponent(r.regulation_number)}`}>
          {r.regulation_number}
        </Link>
        <span className="substance-regs__meta">
          {r.application_date ? `applied ${formatISODate(r.application_date)}` : 'no date'} · {r.product_count} row
          {r.product_count !== 1 ? 's' : ''}
        </span>
        {r.regulation_url && (
          <a className="substance-regs__meta" href={r.regulation_url} target="_blank" rel="noopener noreferrer">
            EUR-Lex ↗
          </a>
        )}
      </li>
    );
  }

  return (
    <div className="pest-page">
      <div className="pest-page__head">
        <div>
          <div className="pest-eyebrow">Substance</div>
          <h1 className="pest-page__title">
            {displayName}
            {markers.map(m => (
              <abbr key={m} className="pest-marker" title={MARKER_TITLES[m] ?? 'Marker from the EU Pesticides Database'}>
                {m}
              </abbr>
            ))}
          </h1>
          <div className="pest-head-meta">
            {status && <span className={statusClass(status)}>{status}</span>}
            {category && <span className="substance-chip substance-chip--category">{category}</span>}
            {cas && <span className="substance-card__cas">CAS {cas}</span>}
            {data.substance.tier2name && normName(data.substance.tier2name) !== normName(displayName) && (
              <span className="pest-subtle">{data.substance.tier2name}</span>
            )}
          </div>
        </div>
        <div className="pest-glance">
          <div className="pest-glance__item">
            <div className="pest-glance__value">{counts.set}</div>
            <div className="pest-glance__label">MRLs set</div>
          </div>
          <div className="pest-glance__item">
            <div className="pest-glance__value">{range ?? '—'}</div>
            <div className="pest-glance__label">Range mg/kg</div>
          </div>
          <div className="pest-glance__item">
            <div className="pest-glance__value">{counts.lod}</div>
            <div className="pest-glance__label">LOD defaults</div>
          </div>
          <div className="pest-glance__item">
            <div className="pest-glance__value">{counts.upcoming}</div>
            <div className="pest-glance__label">Upcoming</div>
          </div>
          <div className="pest-glance__item">
            <div className="pest-glance__value">{data.regulations.length}</div>
            <div className="pest-glance__label">Regulations</div>
          </div>
        </div>
      </div>

      {verdict && (
        <div className="pest-verdict">
          <strong>Not approved in the EU.</strong>{' '}
          All {verdict.count} listed MRLs set at{' '}
          {verdict.value.kind === 'lod' ? `LOD ${mrlText(verdict.value)}` : `${mrlText(verdict.value)} mg/kg`}
          {verdict.reg ? ` by ${shortReg(verdict.reg)}` : ''}
          {verdict.from ? `, applicable from ${formatISODate(verdict.from)}` : ''}. Any residue above{' '}
          {verdict.value.value} mg/kg is non-compliant in every product.
        </div>
      )}

      {data.residue_definition && (
        <div className="substance-page__definition">
          <strong>Residue definition:</strong> {data.residue_definition}
        </div>
      )}

      <div className="pest-tabs2" role="tablist" aria-label="Substance views">
        {tabs.map(([t, label]) => (
          <button
            key={t}
            type="button"
            role="tab"
            id={`pest-tab-${t}`}
            aria-selected={tab === t}
            aria-controls={`pest-panel-${t}`}
            tabIndex={tab === t ? 0 : -1}
            className={`pest-tab${tab === t ? ' pest-tab--active' : ''}`}
            onClick={() => setTab(t)}
            onKeyDown={e => onTabKey(e, tabs.map(([k]) => k))}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'mrls' && (
        <section className="pest-card" role="tabpanel" id="pest-panel-mrls" aria-labelledby="pest-tab-mrls">
          <div className="pest-card__head">
            <h2 className="pest-card__title">MRLs by product · {shown.length}</h2>
            <div className="pest-chips" role="group" aria-label="Rows to show">
              {views.map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  className={`pest-chip${view === v ? ' pest-chip--active' : ''}`}
                  aria-pressed={view === v}
                  onClick={() => setView(v)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="pest-toolbar">
            <input
              type="search"
              className="pest-toolbar__filter"
              placeholder="Filter product or code..."
              aria-label="Filter products"
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />
          </div>

          <div className="pest-table-wrap">
            <table className="pest-table">
              <thead>
                <tr>
                  <th scope="col">Product</th>
                  <th scope="col">MRL mg/kg</th>
                  <th scope="col">Upcoming</th>
                  <th scope="col">Regulation</th>
                  <th scope="col">Applies from</th>
                </tr>
              </thead>
              <tbody>
                {shown.map(r => (
                  <tr key={r.product_id} className="pest-row">
                    <td>
                      <Link className="pest-subst__name" to={`/product/${encodeURIComponent(r.product_id)}`}>
                        {r.name}
                      </Link>
                      <div className="pest-subtle">
                        {r.code ? <span className="pest-mono">{r.code}</span> : null}
                        {r.parent ? `${r.code ? ' · ' : ''}${r.parent}` : ''}
                      </div>
                    </td>
                    <td className={`pest-mono pest-mrl pest-mrl--${r.mrl.kind}`}>{mrlText(r.mrl)}</td>
                    <td>
                      {r.upcoming ? (
                        <span className="pest-pill pest-pill--upcoming">
                          {mrlText(r.upcoming.mrl)} from {formatISODate(r.upcoming.date) || 'a later date'}
                        </span>
                      ) : (
                        <span className="pest-dash">—</span>
                      )}
                    </td>
                    <td className="pest-mono pest-reg">
                      {r.regulation_number ? (
                        <Link to={`/regulation/${encodeURIComponent(r.regulation_number)}`}>
                          {shortReg(r.regulation_number)}
                        </Link>
                      ) : (
                        <span className="pest-dash">—</span>
                      )}
                    </td>
                    <td className="pest-subtle">{formatISODate(r.application_date) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {shown.length === 0 && (
              <div className="pest-empty">
                {rows.length === 0
                  ? 'No MRLs are published for this substance.'
                  : filter.trim()
                  ? 'No product matches the filter.'
                  : view === 'set'
                  ? 'No specific MRLs. Choose All or LOD defaults to list every product.'
                  : 'No product matches.'}
              </div>
            )}
          </div>
        </section>
      )}

      {tab === 'upcoming' && (
        <section className="pest-card" role="tabpanel" id="pest-panel-upcoming" aria-labelledby="pest-tab-upcoming">
          <div className="pest-card__head">
            <h2 className="pest-card__title">Upcoming changes · {upcomingRows.length}</h2>
          </div>
          <div className="pest-table-wrap">
            <table className="pest-table">
              <thead>
                <tr>
                  <th scope="col">Product</th>
                  <th scope="col">MRL today</th>
                  <th scope="col">New MRL</th>
                  <th scope="col">From</th>
                  <th scope="col">Regulation</th>
                </tr>
              </thead>
              <tbody>
                {upcomingRows.map(r => (
                  <tr key={r.product_id} className="pest-row">
                    <td>
                      <Link className="pest-subst__name" to={`/product/${encodeURIComponent(r.product_id)}`}>
                        {r.name}
                      </Link>
                      {r.code && <div className="pest-subtle pest-mono">{r.code}</div>}
                    </td>
                    <td className="pest-mono">{mrlText(r.mrl)}</td>
                    <td className="pest-mono pest-mrl">{mrlText(r.upcoming!.mrl)}</td>
                    <td className="pest-subtle">{formatISODate(r.upcoming!.date) || '—'}</td>
                    <td className="pest-mono pest-reg">
                      {r.upcoming!.reg ? (
                        <Link to={`/regulation/${encodeURIComponent(r.upcoming!.reg as string)}`}>
                          {shortReg(r.upcoming!.reg)}
                        </Link>
                      ) : (
                        <span className="pest-dash">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {upcomingRows.length === 0 && (
              <div className="pest-empty">No upcoming MRL changes for this substance.</div>
            )}
          </div>
        </section>
      )}

      {tab === 'regulations' && (
        <section className="pest-card" role="tabpanel" id="pest-panel-regulations" aria-labelledby="pest-tab-regulations">
          <div className="pest-card__head">
            <h2 className="pest-card__title">Regulations affecting this substance</h2>
          </div>
          <div className="substance-regs">
            <ul className="substance-regs__list">
              {inForceRegs.map(renderRegItem)}
              {upcomingRegs.map(renderRegItem)}
              {newsRegs.map(renderRegItem)}
              {showHistorical && historicalRegs.map(renderRegItem)}
            </ul>
            {historicalRegs.length > 0 && (
              <button type="button" className="substance-regs__toggle" onClick={() => setShowHistorical(v => !v)}>
                {showHistorical ? `Hide ${historicalRegs.length} historical` : `Show ${historicalRegs.length} historical`}
              </button>
            )}
            {data.regulations.length === 0 && (
              <div className="pest-empty">No regulations recorded for this substance.</div>
            )}
          </div>
        </section>
      )}

      {tab === 'data' && (
        <section className="pest-card pest-card--plain" role="tabpanel" id="pest-panel-data" aria-labelledby="pest-tab-data">
          {data.actives.length > 0 ? (
            <div className="substance-actives">
              {data.actives.map(a => (
                <ActiveCard key={a.substance_id ?? a.substance_name ?? Math.random().toString()} active={a} />
              ))}
            </div>
          ) : (
            <div className="pest-empty">
              No active-substance record is linked to this residue, so approval status and toxicology values are
              unavailable.
            </div>
          )}
        </section>
      )}

      <p className="mrl-footnote">
        * MRL set at the limit of analytical determination (LOD) — the lowest residue level laboratories can
        reliably measure, used where no specific MRL is established.
      </p>
    </div>
  );
}
